import React, { useState, useEffect } from 'react';
import './IntegrationsPage.scss';
import Title from '../../shared/components/title/Title';
import MobilePageHeader from '../../shared/components/common/mobile-menu/MobilePageHeader';
import useIsMobile from '../../shared/hooks/global/UseIsMobile';
import { useTranslation } from 'react-i18next';
import { useAccount } from '../../shared/contexts/account/AccountContexts';
import Loader from '../../shared/components/common/loader/Loader';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { LookerStudioService } from '../../shared/services/integrations/LookerStudioService';
import {
  Brand24Service,
  Brand24ConnectionStatus,
} from '../../shared/services/integrations/Brand24Service';
import Brand24ConnectDialog from '../../shared/components/integrations/Brand24ConnectDialog';
import { REACT_APP_GOOGLE_CLIENT_ID } from '../../shared/utils/config';
import { useNavigate } from 'react-router-dom';
import { PROJECTS } from '../../shared/routes/path';

const LOOKER_FEATURES: { icon: string; titleKey: string; descKey: string }[] = [
  { icon: 'ico--calendar', titleKey: 'lookerStudioFeat1Title', descKey: 'lookerStudioFeat1Desc' },
  { icon: 'ico--chart', titleKey: 'lookerStudioFeat2Title', descKey: 'lookerStudioFeat2Desc' },
  { icon: 'ico--refresh', titleKey: 'lookerStudioFeat3Title', descKey: 'lookerStudioFeat3Desc' },
];

const BRAND24_FEATURES: { icon: string; titleKey: string; descKey: string }[] = [
  { icon: 'ico--chart', titleKey: 'brand24Feat1Title', descKey: 'brand24Feat1Desc' },
  { icon: 'ico--puzzle', titleKey: 'brand24Feat2Title', descKey: 'brand24Feat2Desc' },
  { icon: 'ico--bell', titleKey: 'brand24Feat3Title', descKey: 'brand24Feat3Desc' },
];

const IntegrationsPage: React.FC = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { accountResponse } = useAccount();
  const navigate = useNavigate();

  const [isLookerStudioConnected, setIsLookerStudioConnected] = useState(false);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);
  const [disconnectSuccess, setDisconnectSuccess] = useState<string | null>(null);

  const [brand24Status, setBrand24Status] = useState<Brand24ConnectionStatus | null>(null);
  const [showBrand24Dialog, setShowBrand24Dialog] = useState(false);

  useEffect(() => {
    void refreshBrand24Status();
    checkLookerStudioConnection();

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      if (event.data.type === 'LOOKER_STUDIO_CONNECTED') {
        checkLookerStudioConnection();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    if (disconnectSuccess) {
      const timer = setTimeout(() => {
        setDisconnectSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [disconnectSuccess]);

  // The Integrations tab requires the Looker Studio or AI Signals addon — block direct URL access otherwise.
  useEffect(() => {
    if (!accountResponse) return;
    const allowed =
      accountResponse.addons.includes('LOOKER_STUDIO') ||
      accountResponse.addons.includes('AI_SIGNALS');
    if (!allowed) navigate(PROJECTS, { replace: true });
  }, [accountResponse, navigate]);

  const checkLookerStudioConnection = async () => {
    try {
      const status = await LookerStudioService.getConnectionStatus();
      setIsLookerStudioConnected(status.isConnected);
    } catch (error) {
      console.error('Error checking Looker Studio connection:', error);
    }
  };

  const handleConnectLookerStudio = async () => {
    const clientId = REACT_APP_GOOGLE_CLIENT_ID;
    const redirectUri = `${window.location.origin}/looker-studio/callback`;
    // Tylko `drive.file` (non-sensitive) — każdy scope sensitive wymusza weryfikację aplikacji przez Google.
    const scopes = 'https://www.googleapis.com/auth/drive.file';

    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&scope=${encodeURIComponent(scopes)}&response_type=code&access_type=offline&prompt=consent`;

    window.open(authUrl, '_blank', 'width=500,height=600');
  };

  const handleDisconnectLookerStudio = () => {
    confirmDialog({
      message: t('confirmDisconnectMessage'),
      header: t('confirmDisconnection'),
      defaultFocus: 'accept',
      accept: confirmDisconnect,
    });
  };

  const confirmDisconnect = async () => {
    setDisconnectError(null);
    setDisconnectSuccess(null);

    try {
      await LookerStudioService.disconnect();
      setIsLookerStudioConnected(false);
      setDisconnectSuccess(t('successfullyDisconnected'));
    } catch (error) {
      console.error('Error disconnecting:', error);
      setDisconnectError(t('failedToDisconnect'));
    }
  };

  const handleViewReports = () => {
    navigate('/periodic-reports?provider=LOOKER_STUDIO');
  };

  const refreshBrand24Status = async () => {
    try {
      const status = await Brand24Service.getConnectionStatus();
      setBrand24Status(status);
    } catch (error) {
      console.error('Error checking Brand24 connection:', error);
    }
  };

  const openBrand24Dialog = () => setShowBrand24Dialog(true);

  const handleBrand24Disconnect = async () => {
    await Brand24Service.disconnect();
    await refreshBrand24Status();
  };

  if (!accountResponse) {
    return (
      <div className="page-content">
        <div className="page-box">
          <Loader />
        </div>
      </div>
    );
  }

  const renderCard = (config: {
    key: string;
    domain: string;
    name: string;
    price: string;
    desc: string;
    features: { icon: string; titleKey: string; descKey: string }[];
    isActive: boolean;
    footer: React.ReactNode;
  }) => (
    <div
      key={config.key}
      className={`integrations__card ${config.isActive ? 'integrations__card--active' : ''}`}
    >
      <div className="integrations__celebrate playbook-stat__celebrate" aria-hidden="true">
        <span className="playbook-stat__celebrate-spark playbook-stat__celebrate-spark--1" />
        <span className="playbook-stat__celebrate-spark playbook-stat__celebrate-spark--2" />
        <span className="playbook-stat__celebrate-spark playbook-stat__celebrate-spark--3" />
        <span className="playbook-stat__celebrate-spark playbook-stat__celebrate-spark--4" />
        <div className="playbook-stat__celebrate-circle">
          <span className="ico--tick ico--md" />
        </div>
      </div>

      <div className="integrations__head">
        <img
          src={`https://favicon.is/${config.domain}`}
          width={36}
          height={36}
          alt=""
          className="integrations__logo"
        />
        <div className="integrations__title-group">
          <div className="integrations__name">{config.name}</div>
          <div
            className={`integrations__price ${
              config.isActive ? 'integrations__price--hidden' : ''
            }`}
          >
            {config.price}
          </div>
        </div>
      </div>

      <div className="integrations__desc">{config.desc}</div>

      <div className="integrations__included">
        <div className="integrations__included-title">{t('integrationsIncluded')}</div>
        {config.features.map((feature) => (
          <div key={feature.titleKey} className="integrations__feature">
            <div className="integrations__feature-icon">
              <i className={`${feature.icon} ico--sm`} />
            </div>
            <div>
              <div className="integrations__feature-name">{t(feature.titleKey)}</div>
              <div className="integrations__feature-desc">{t(feature.descKey)}</div>
            </div>
          </div>
        ))}
      </div>

      {config.footer}
    </div>
  );

  const isBrand24Active = brand24Status?.isConnected ?? false;
  const hasLookerStudio = accountResponse?.addons.includes('LOOKER_STUDIO') ?? false;
  const hasAiSignals = accountResponse?.addons.includes('AI_SIGNALS') ?? false;

  const cards = [
    hasLookerStudio &&
      renderCard({
        key: 'looker-studio',
      domain: 'lookerstudio.google.com',
                name: t('lookerStudio'),
                price: t('free'),
                desc: t('lookerStudioIntegrationDesc'),
                features: LOOKER_FEATURES,
                isActive: isLookerStudioConnected,
                footer: (
                  <>
                    {disconnectError && (
                      <div className="integrations__message integrations__message--error">
                        {disconnectError}
                      </div>
                    )}
                    {disconnectSuccess && (
                      <div className="integrations__message integrations__message--success">
                        {disconnectSuccess}
                      </div>
                    )}
                    <div className="integrations__actions">
                      {isLookerStudioConnected ? (
                        <>
                          <button
                            className="btn btn--sm btn--secondary"
                            onClick={handleViewReports}
                            title={t('viewAllReports')}
                          >
                            <span className="ico--calendar ico--xs" />
                            {t('viewReports')}
                          </button>
                          <button
                            className="btn btn--sm btn--gray integrations__disconnect"
                            onClick={handleDisconnectLookerStudio}
                          >
                            {t('disconnect')}
                          </button>
                        </>
                      ) : (
                        <button className="btn btn--sm" onClick={handleConnectLookerStudio}>
                          {t('connect')}
                        </button>
                      )}
                    </div>
                  </>
                ),
              }),

    hasAiSignals &&
      renderCard({
        key: 'brand24-signals',
        domain: 'brand24.com',
                name: 'Brand24 AI Signals',
                price: t('brand24SignalsPrice'),
                desc: t('brand24SignalsDesc'),
                features: BRAND24_FEATURES,
                isActive: isBrand24Active,
                footer: (
                  <div className="integrations__actions">
                    {isBrand24Active ? (
                      // Connected: hide the configuration form entirely — only allow disconnecting.
                      <button
                        className="btn btn--sm btn--gray integrations__disconnect"
                        type="button"
                        onClick={handleBrand24Disconnect}
                      >
                        {t('disconnect')}
                      </button>
                    ) : (
                      <button className="btn btn--sm" type="button" onClick={openBrand24Dialog}>
                        {t('connect')}
                      </button>
                    )}
                  </div>
                ),
              }),
  ].filter(Boolean);

  return (
    <>
      <div className="page-content integrations-page">
        <div className="page-box">
          {/* Telefon: tytuł w górnym pasku zamiast nagłówka strony. */}
          <MobilePageHeader title={<span>{t('integrations')}</span>} />
          {!isMobile && <Title title={t('integrations')} />}

          <div className="page-box__stats">
            <div className="integrations__grid">{cards}</div>
          </div>
        </div>
      </div>

      <ConfirmDialog className="dialog-box" />

      <Brand24ConnectDialog
        visible={showBrand24Dialog}
        onHide={() => setShowBrand24Dialog(false)}
        onConnected={refreshBrand24Status}
        defaultMode={brand24Status?.mode ?? 'DEFAULT'}
      />
    </>
  );
};

export default IntegrationsPage;
